import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import {
    MutableRefObject,
    useCallback,
    useContext,
    useRef,
    useState,
} from 'react'

import { AuthContext } from '@deltek/specification-client/core/Configurations/AuthContext'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import { SetTableViewSettings } from 'api/graphQL/mutations/productListing.mutation'
import { GetTableViewSettings } from 'api/graphQL/queries/listing.queries'
import { ProductSearchQuery } from 'api/graphQL/queries/productSearch.queries'
import { ProductSearchResponse } from 'api/graphQL/types/product.types'
import {
    ProductSearchBarState,
    productSearchBarReactiveVar,
} from 'components/Filter/productSearchBarReactiveVar'
import { IdTitleProps } from 'components/SpecTable/settings/TableSettings'
import {
    setTableSettings,
    tableSettingsReactiveVar,
} from 'components/SpecTable/settings/tableSettingsReactiveVar'
import { columnMap } from 'pages/TablePage/tablePage'
import useGlobalSnackbar from 'shared/hooks/useGlobalSnackbar'
import useLocalStorage from 'shared/hooks/useLocalStorage'
import { defaultTableSettings } from './productListingPageConstants'

export type SelectedColumnType = {
    columnName: string
    order: number
    columnWidth: number
    isColumnPinned: boolean
    columnOrder: string | undefined
}

export const prepareTableSettingsFromData = (tableData: any) => {
    let selectedColumnsSettings: IdTitleProps[] = []
    let groupColumnsSettings: IdTitleProps[] = []
    let pinningPropsSettings = {
        firstColumnPinned: false,
        lastColumnPinned: false,
    }
    let collapseColumnCheckedSettings: Record<string, boolean> = {}
    let orderBySettings = ''
    let columnWidthsSettings: Record<string, number> = {}
    const groupEnabledSettings = tableData.isTableGroupingEnabled
    const isTableView = tableData.isTableView

    tableData.tableGroupings.forEach(
        ({
            columnName,
            isCollapsed,
        }: {
            columnName: string
            isCollapsed: boolean
        }) => {
            if (columnMap[columnName]?.Header) {
                groupColumnsSettings.push({
                    id: columnName,
                    title: (columnMap[columnName].Header as string) ?? '',
                })
                orderBySettings = `${orderBySettings}${columnName},`
            }

            collapseColumnCheckedSettings[columnName ?? ''] =
                isCollapsed ?? false
        }
    )

    tableData.selectedColumns.forEach(
        (
            {
                columnName,
                columnOrder,
                columnWidth,
                isColumnPinned,
            }: SelectedColumnType,
            index: number
        ) => {
            const normalizedColumnName =
                columnName === 'sectionNumber' ? 'sectionName' : columnName
            if (
                normalizedColumnName !== 'groupColExpander' &&
                !tableData.tableGroupings.some(
                    (group: Record<string, string>) =>
                        group.columnName === normalizedColumnName
                )
            ) {
                selectedColumnsSettings.push({
                    id: normalizedColumnName,
                    title:
                        (columnMap[normalizedColumnName]?.Header as string) ??
                        '',
                })
            }

            if (isColumnPinned) {
                if (index === 0) {
                    pinningPropsSettings.firstColumnPinned = true
                }
                if (index === tableData.selectedColumns.length - 1) {
                    pinningPropsSettings.lastColumnPinned = true
                }
            }

            const columnOrderString =
                `${columnOrder}`.toLowerCase() === 'undefined'
                    ? undefined
                    : `${columnOrder}`.toLowerCase() === 'descending'
                    ? ' desc,'
                    : ' asc,'

            if (columnOrderString) {
                orderBySettings = `${orderBySettings}${normalizedColumnName}${columnOrderString}`
            }

            columnWidthsSettings[normalizedColumnName] = Number(
                columnWidth ?? 0
            )
        }
    )

    const availableColumns = Object.values(columnMap)
        .map((col: any) => ({ id: col.id, title: col.Header })) // Need to set this as any since Id types are clashing
        .filter(
            (col) =>
                !selectedColumnsSettings
                    .map((col) => col.id)
                    .includes(col.id) &&
                !groupColumnsSettings.map((col) => col.id).includes(col.id)
        )

    const sortType = orderBySettings.includes('type')
    if (!sortType) {
        orderBySettings = `${orderBySettings}type,`
    }

    const sortLastUpdated = orderBySettings.includes('lastUpdated')
    if (!sortLastUpdated) {
        orderBySettings = `${orderBySettings}lastUpdated desc`
    }

    return {
        availableColumns,
        groupEnabledSettings,
        selectedColumnsSettings,
        groupColumnsSettings,
        pinningPropsSettings,
        collapseColumnCheckedSettings,
        orderBySettings,
        columnWidthsSettings,
        isTableView,
    }
}

const useListingQueryHelper = () => {
    const { storedValue, setValue } = useLocalStorage('tableSettings', {})
    const {
        currentUserInfo: {
            account: { accountId: userId },
        },
    } = useContext(AuthContext)
    const [searchLoading, setSearchLoading] = useState(false)
    const [isCardViewActive, setToggleCardView] = useState(
        !Object.keys(storedValue).length ? storedValue.isTableView : false
    )
    const [
        showBottomLoadingIndicator,
        setShowBottomLoadingIndicator,
    ] = useState(false)
    const { showGlobalSnackbar } = useGlobalSnackbar()

    const [showTableLoadingIndicator, setShowTableLoadingIndicator] = useState(
        false
    )
    const isTableSettingsLoadedRef: MutableRefObject<boolean> = useRef(false)

    const { selection: selectionState } = useReactiveVar<ProductSearchBarState>(
        productSearchBarReactiveVar
    )

    const [
        saveTableSettings,
        { loading: saveTableSettingsLoading },
    ] = useMutation(SetTableViewSettings)

    const [
        getTableSettings,
        { data: tableViewSettingsData, called: tableViewSettingsCalled },
    ] = useLazyQuery(GetTableViewSettings, {
        fetchPolicy: 'no-cache',
        onCompleted: () => {
            try {
                const tableSettingsData =
                    tableViewSettingsData?.getTableViewSettingOfUser

                if (tableSettingsData) {
                    if (
                        !!storedValue &&
                        storedValue?.selectedColumns &&
                        storedValue?.groupColumns &&
                        [
                            ...storedValue?.selectedColumns,
                            ...storedValue?.groupColumns,
                        ].length &&
                        new Date(tableSettingsData.lastUpdated).getTime() <
                            new Date(storedValue?.lastUpdated).getTime()
                    ) {
                        setTableSettings({
                            ...storedValue,
                            isTableSettingLoaded: true,
                        })

                        setToggleCardView(!storedValue?.isTableView)

                        const {
                            groupColumns,
                            columnWidths,
                            pinningProps,
                            orderBy,
                            selectedColumns,
                            groupEnabled,
                            collapseColumnChecked,
                        } = storedValue

                        // save local settings if the last updated of the local setting is newer than the server
                        const hasGroupColumns = !!groupColumns.length
                        const groupColumnReq = hasGroupColumns
                            ? [
                                  {
                                      columnName: 'groupColExpander',
                                      order: 1,
                                      columnOrder: 'UNDEFINED',
                                      columnWidth:
                                          columnWidths?.['groupColExpander'] ??
                                          300,
                                      isColumnPinned:
                                          pinningProps.firstColumnPinned,
                                  },
                              ]
                            : []
                        const orderByArray = orderBy.split(',')

                        const selectedColumnsReq = [
                            ...groupColumnReq,
                            ...selectedColumns.map(
                                (col: IdTitleProps, index: number) => {
                                    const columnOrderObject = orderByArray.find(
                                        (val: string) =>
                                            val.split(' ')[0] === col.id
                                    )
                                    return {
                                        columnName: col.id,
                                        order: hasGroupColumns
                                            ? index + 2
                                            : index + 1,
                                        columnOrder: columnOrderObject
                                            ? columnOrderObject.split(' ')
                                                  .length > 1
                                                ? 'DESCENDING'
                                                : 'ASCENDING'
                                            : 'UNDEFINED',
                                        columnWidth:
                                            columnWidths?.[col.id] ?? 300,
                                        isColumnPinned:
                                            (!hasGroupColumns &&
                                                index === 0 &&
                                                pinningProps.firstColumnPinned) ||
                                            (hasGroupColumns &&
                                                selectedColumns.length - 1 ===
                                                    index &&
                                                pinningProps.lastColumnPinned),
                                    }
                                }
                            ),
                        ]

                        if (!!selectedColumnsReq.length) {
                            saveTableSettings({
                                variables: {
                                    input: {
                                        isTableGroupingEnabled: groupEnabled,
                                        isTableView: !isCardViewActive,
                                        selectedColumns: selectedColumnsReq,
                                        tableGroupings: [
                                            ...groupColumns?.map(
                                                (col: IdTitleProps) => ({
                                                    columnName: col.id,
                                                    isCollapsed:
                                                        collapseColumnChecked?.[
                                                            col.id
                                                        ],
                                                })
                                            ),
                                        ],
                                        userId,
                                    },
                                },
                            })
                        }
                        return
                    }
                    updateSettingsState(tableSettingsData)
                    return
                }
                if (
                    !tableSettingsData ||
                    (!!tableSettingsData &&
                        ![
                            ...tableSettingsData?.selectedColumns,
                            ...tableSettingsData?.tableGroupings,
                        ].length)
                ) {
                    updateSettingsState(defaultTableSettings)
                }
            } catch (e) {
                updateSettingsState(defaultTableSettings)

                // noop
            } finally {
                isTableSettingsLoadedRef.current = true
            }
        },
        onError: () => {
            if (storedValue.selectedColumns.length) {
                setTableSettings({
                    ...storedValue,
                    isTableSettingLoaded: true,
                })

                setToggleCardView(!storedValue?.isTableView)
            } else {
                updateSettingsState(defaultTableSettings)
            }
            isTableSettingsLoadedRef.current = true
        },
    })

    const [
        getListings,
        {
            data: searchData,
            fetchMore: searchFetchMore,
            called: searchCalled,
            loading: listingLoading,
            networkStatus: listingNetworkStatus,
        },
    ] = useLazyQuery<ProductSearchResponse>(ProductSearchQuery, {
        displayName: 'ProductSearchResult',
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'no-cache',
        onCompleted: () => {
            const {
                collapseColumnChecked: collapseColumnState,
            } = tableSettingsReactiveVar()
            setSearchLoading(false)
            setShowTableLoadingIndicator(false)
            const hasExpandedColumns = Object.values(collapseColumnState).some(
                (value) => value === false
            )

            hasExpandedColumns
                ? setTimeout(() => {
                      setShowBottomLoadingIndicator(false)
                  }, 0)
                : setShowBottomLoadingIndicator(false)
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on product search',
                    variant: 'error',
                    id: `ProductListingsPage_ProductSearchError`,
                },
            })
            setSearchLoading(false)
            setShowTableLoadingIndicator(false)
            setShowBottomLoadingIndicator(false)
        },
    })

    const updateSettingsState = useCallback((tableData) => {
        const {
            availableColumns,
            groupEnabledSettings,
            selectedColumnsSettings,
            groupColumnsSettings,
            pinningPropsSettings,
            collapseColumnCheckedSettings,
            orderBySettings,
            columnWidthsSettings,
            isTableView,
        } = prepareTableSettingsFromData(tableData)

        setTableSettings({
            availableColumns,
            groupEnabled: groupEnabledSettings,
            selectedColumns: selectedColumnsSettings,
            groupColumns: groupColumnsSettings,
            pinningProps: pinningPropsSettings,
            collapseColumnChecked: collapseColumnCheckedSettings,
            orderBy: orderBySettings,
            columnWidths: columnWidthsSettings,
            isTableSettingLoaded: true,
        })

        setToggleCardView(!isTableView)
    }, [])

    const fetchTableSettings = useCallback(() => {
        getTableSettings()

        const loadTimeout = setTimeout(() => {
            isTableSettingsLoadedRef.current = true
            clearTimeout(loadTimeout)
        }, 1000)
    }, [tableViewSettingsData])

    const resetTableSettings = useCallback(() => {
        saveTableSettings({
            variables: {
                input: {
                    ...defaultTableSettings,
                    userId,
                },
            },
        })
    }, [userId])

    const saveTableViewSettings = useCallback(() => {
        const {
            availableColumns,
            groupColumns,
            columnWidths,
            pinningProps,
            orderBy,
            selectedColumns,
            groupEnabled,
            collapseColumnChecked,
        } = tableSettingsReactiveVar()

        const { lastUpdated, ...savedLocalSettings } = storedValue

        if (
            !isEqual(
                { ...savedLocalSettings },
                {
                    availableColumns,
                    groupColumns,
                    columnWidths,
                    pinningProps,
                    orderBy,
                    selectedColumns,
                    groupEnabled,
                    collapseColumnChecked,
                    isTableView: !isCardViewActive,
                }
            )
        ) {
            const hasGroupColumns = !!groupColumns.length
            const groupColumnReq = hasGroupColumns
                ? [
                      {
                          columnName: 'groupColExpander',
                          order: 1,
                          columnOrder: 'UNDEFINED',
                          columnWidth:
                              columnWidths?.['groupColExpander'] ?? 300,
                          isColumnPinned: pinningProps.firstColumnPinned,
                      },
                  ]
                : []
            const orderByArray = orderBy.split(',')

            const selectedColumnsReq = [
                ...groupColumnReq,
                ...selectedColumns.map((col, index) => {
                    const columnOrderObject = orderByArray.find(
                        (val) => val.split(' ')[0] === col.id
                    )
                    return {
                        columnName: col.id,
                        order: hasGroupColumns ? index + 2 : index + 1,
                        columnOrder: columnOrderObject
                            ? columnOrderObject.split(' ').length > 1
                                ? 'DESCENDING'
                                : 'ASCENDING'
                            : 'UNDEFINED',
                        columnWidth: columnWidths?.[col.id] ?? 300,
                        isColumnPinned:
                            (!hasGroupColumns &&
                                index === 0 &&
                                pinningProps.firstColumnPinned) ||
                            (hasGroupColumns &&
                                selectedColumns.length - 1 === index &&
                                pinningProps.lastColumnPinned),
                    }
                }),
            ]

            if (!!selectedColumnsReq.length) {
                saveTableSettings({
                    variables: {
                        input: {
                            isTableGroupingEnabled: groupEnabled,
                            isTableView: !isCardViewActive,
                            selectedColumns: selectedColumnsReq,
                            tableGroupings: [
                                ...groupColumns?.map((col) => ({
                                    columnName: col.id,
                                    isCollapsed:
                                        collapseColumnChecked?.[col.id],
                                })),
                            ],
                            userId,
                        },
                    },
                })
            }
        }
    }, [isCardViewActive, tableSettingsReactiveVar, userId, storedValue])

    const setLocalTableSettings = useCallback(() => {
        const {
            availableColumns,
            groupColumns,
            collapseColumnChecked,
            // filters, This is not in the AC for saving the settings but we may need to add this in the future
            groupEnabled,
            orderBy,
            pinningProps,
            selectedColumns,
            columnWidths,
        } = tableSettingsReactiveVar()

        setValue({
            availableColumns,
            groupColumns,
            columnWidths,
            pinningProps,
            orderBy,
            selectedColumns,
            groupEnabled,
            collapseColumnChecked,
            lastUpdated: new Date().toString(),
            isTableView: !isCardViewActive,
        })
    }, [tableSettingsReactiveVar, setValue, isCardViewActive])

    return {
        isTableSettingsLoaded: isTableSettingsLoadedRef?.current,
        isCardViewActive,
        setToggleCardView,
        getListings,
        searchData,
        searchFetchMore,
        searchCalled,
        listingLoading,
        listingNetworkStatus,
        searchLoading,
        setSearchLoading,
        showBottomLoadingIndicator,
        setShowBottomLoadingIndicator,
        showTableLoadingIndicator,
        setShowTableLoadingIndicator,
        tableViewSettingsData,
        tableViewSettingsCalled,
        fetchTableSettings,
        saveTableViewSettings,
        resetTableSettings,
        setLocalTableSettings,
    }
}

export default useListingQueryHelper
